import {
  Alert,
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@efexteam/components';
import { RoleId, RoleIds } from '@efexteam/permission-manager';
import { Box, Typography } from '@mui/material';
import { IMembers } from '@pages/profile/types';
import { useTranslation } from 'react-i18next';
import EditModalStyles from './EditModal.style';
import useUpdate from './hooks/useUpdate';

interface EditModalProps {
  memberData: IMembers;
  open: boolean;
  status: boolean;
  role: RoleId;
  onClose: () => void;
}

const EditModal = ({
  memberData,
  open,
  status,
  role,
  onClose,
}: EditModalProps) => {
  const { t } = useTranslation();
  const { firstName, lastName } = memberData.naturalPerson;
  const fullName = `${firstName} ${lastName}`.trim();

  const { loading, error, updateTeamMemberData } = useUpdate({
    status,
    role,
    naturalPersonId: memberData.naturalPerson.id,
  });

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
      <DialogTitle
        className={EditModalStyles.dialogTitle}
        showCloseButton
        onClick={onClose}
      >
        <span> {t('roleUpdateModalTitle', { name: fullName })}</span>
      </DialogTitle>
      <DialogContent>
        <Box className={EditModalStyles.dialogContent}>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar username={fullName} size="md" />
            <Typography>{fullName}</Typography>
          </Box>
          <Typography
            variant="body2"
            className={EditModalStyles.statusText(status)}
          >
            {t(status ? 'editMmbInfoActive' : 'editMmbInfoInactive')}
          </Typography>
          <Typography variant="body2" className={EditModalStyles.roleText}>
            {t(`member${RoleIds[role]}`)}
          </Typography>
        </Box>

        {error && (
          <Alert variant="outlined" severity="error">
            {error.message || 'Error updating member'}
          </Alert>
        )}
      </DialogContent>
      <DialogActions className={EditModalStyles.dialogActions}>
        <Button
          size="large"
          appearance="secondary"
          disabled={loading}
          onClick={onClose}
        >
          {t('cancel')}
        </Button>
        <Button
          size="large"
          appearance="primary"
          disabled={loading}
          onClick={() => updateTeamMemberData()}
        >
          {t('editMmbUpdateRole')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditModal;
